import { useState } from 'react';

import type { OfferPainPoint, PostsFromOfferRequest } from '@smartapply/shared';

interface FromOfferFormProps {
  onSubmit: (req: PostsFromOfferRequest) => void;
  loading: boolean;
}

/**
 * Form do modo "Lançamento de oferta" (template Yago Martins). Coleta:
 *   vocativo + nome da oferta + dimensão + 4-12 painpoints (dor → solução)
 *   + outcome em 1 frase + palavra-chave do CTA + opcionais (tempo, ferramenta).
 *
 * Mostra preview do template ao lado pra user entender a estrutura antes
 * de gerar.
 */
export function FromOfferForm({ onSubmit, loading }: FromOfferFormProps) {
  const [audienceVocative, setAudienceVocative] = useState('');
  const [offerName, setOfferName] = useState('');
  const [offerDimension, setOfferDimension] = useState('');
  const [painPoints, setPainPoints] = useState<OfferPainPoint[]>([
    { problem: '', solution: '' },
    { problem: '', solution: '' },
    { problem: '', solution: '' },
    { problem: '', solution: '' },
  ]);
  const [outcomeOneLiner, setOutcomeOneLiner] = useState('');
  const [commentKeyword, setCommentKeyword] = useState('');
  const [testedTime, setTestedTime] = useState('');
  const [tool, setTool] = useState('');

  const filledPainPoints = painPoints.filter(
    (p) => p.problem.trim().length >= 3 && p.solution.trim().length >= 3,
  );
  const canSubmit =
    !loading &&
    audienceVocative.trim().length >= 2 &&
    offerName.trim().length >= 4 &&
    offerDimension.trim().length >= 10 &&
    filledPainPoints.length >= 4 &&
    outcomeOneLiner.trim().length >= 10 &&
    commentKeyword.trim().length >= 2 &&
    /^[A-Z0-9_-]+$/.test(commentKeyword.trim());

  function updatePainPoint(idx: number, field: 'problem' | 'solution', value: string) {
    setPainPoints((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  }

  function addPainPoint() {
    if (painPoints.length >= 12) return;
    setPainPoints((prev) => [...prev, { problem: '', solution: '' }]);
  }

  function removePainPoint(idx: number) {
    if (painPoints.length <= 4) return;
    setPainPoints((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      audienceVocative: audienceVocative.trim(),
      offerName: offerName.trim(),
      offerDimension: offerDimension.trim(),
      painPoints: filledPainPoints.map((p) => ({
        problem: p.problem.trim(),
        solution: p.solution.trim(),
      })),
      outcomeOneLiner: outcomeOneLiner.trim(),
      commentKeyword: commentKeyword.trim().toUpperCase(),
      testedTime: testedTime.trim() || undefined,
      tool: tool.trim() || undefined,
    });
  }

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_360px] lg:gap-16">
      <form onSubmit={handleSubmit} className="space-y-6">
        <Field
          label="Público-alvo (vocativo)"
          hint='Quem você está chamando? Ex.: "Advogados", "Devs backend", "Profissionais de Marketing", "Coaches".'
        >
          <input
            type="text"
            value={audienceVocative}
            onChange={(e) => setAudienceVocative(e.target.value)}
            placeholder="Profissionais de Marketing"
            maxLength={80}
            className={inputClass}
            disabled={loading}
          />
        </Field>

        <Field
          label="Nome da oferta"
          hint='O que você está entregando? Ex.: "12 prompts pra vendas B2B", "Checklist de SEO técnico".'
        >
          <input
            type="text"
            value={offerName}
            onChange={(e) => setOfferName(e.target.value)}
            placeholder="12 prompts pra vendas B2B"
            maxLength={160}
            className={inputClass}
            disabled={loading}
          />
        </Field>

        <Field
          label="Dimensão da oferta"
          hint="O que está incluído (texto livre curto). Ex.: cold email, discovery, follow-up, objeções, proposta."
        >
          <textarea
            value={offerDimension}
            onChange={(e) => setOfferDimension(e.target.value)}
            placeholder="cold email com taxa de abertura acima de 40%, discovery call com perguntas certas, playbook de objeções, proposta que fecha sem competir no preço"
            rows={3}
            maxLength={600}
            className={inputClass + ' resize-none'}
            disabled={loading}
          />
        </Field>

        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            dores → soluções ({filledPainPoints.length} de {painPoints.length} preenchidas; min 4, max 12)
          </p>
          <p className="mb-3 font-sans text-xs italic text-[var(--ink-tertiary)]">
            Cada par vira uma linha com → no post. Especifique a dor real do público e a solução
            que a oferta entrega.
          </p>
          <ul className="space-y-3">
            {painPoints.map((pp, i) => (
              <li key={i} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                <input
                  type="text"
                  value={pp.problem}
                  onChange={(e) => updatePainPoint(i, 'problem', e.target.value)}
                  placeholder={`Dor ${i + 1} (ex.: cold email sem resposta)`}
                  maxLength={200}
                  className={inputClass + ' text-sm'}
                  disabled={loading}
                />
                <input
                  type="text"
                  value={pp.solution}
                  onChange={(e) => updatePainPoint(i, 'solution', e.target.value)}
                  placeholder={`Solução ${i + 1} (ex.: prompt com abertura que quebra padrão)`}
                  maxLength={200}
                  className={inputClass + ' text-sm'}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => removePainPoint(i)}
                  disabled={loading || painPoints.length <= 4}
                  aria-label={`remover dor ${i + 1}`}
                  className="self-center px-2 py-1 font-mono text-xs text-[var(--ink-tertiary)] hover:text-[var(--accent-crimson)] disabled:opacity-30"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          {painPoints.length < 12 && (
            <button
              type="button"
              onClick={addPainPoint}
              disabled={loading}
              className="mt-3 font-sans text-xs text-[var(--accent-saffron)] underline-offset-4 hover:underline"
            >
              + adicionar dor
            </button>
          )}
        </div>

        <Field
          label="Resultado de negócio (1 frase)"
          hint='Liga a oferta ao desfecho que importa. Ex.: "Quem aplica IA em vendas ganha previsibilidade de forecast e ticket médio."'
        >
          <input
            type="text"
            value={outcomeOneLiner}
            onChange={(e) => setOutcomeOneLiner(e.target.value)}
            placeholder="Quem aplica IA de verdade em vendas ganha tempo de execução, previsibilidade e ticket médio."
            maxLength={240}
            className={inputClass}
            disabled={loading}
          />
        </Field>

        <Field
          label="Palavra-chave do CTA"
          hint="Palavra que o leitor comenta pra receber o material no privado. UPPERCASE. Sem espaços."
        >
          <input
            type="text"
            value={commentKeyword}
            onChange={(e) => setCommentKeyword(e.target.value.toUpperCase().replace(/\s+/g, ''))}
            placeholder="VENDAS"
            maxLength={20}
            className={inputClass + ' uppercase'}
            disabled={loading}
          />
        </Field>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Tempo testando (opcional)" hint='Ex.: "3 dias", "uma semana".'>
            <input
              type="text"
              value={testedTime}
              onChange={(e) => setTestedTime(e.target.value)}
              placeholder="3 dias"
              maxLength={40}
              className={inputClass}
              disabled={loading}
            />
          </Field>
          <Field label="Ferramenta usada (opcional)" hint="Qual IA você usou? Claude, ChatGPT, Gemini.">
            <input
              type="text"
              value={tool}
              onChange={(e) => setTool(e.target.value)}
              placeholder="Claude"
              maxLength={60}
              className={inputClass}
              disabled={loading}
            />
          </Field>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-sm bg-[var(--ink-primary)] px-6 py-3 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {loading ? 'Gerando 2-3 variantes...' : 'Gerar drafts no template'}
        </button>
      </form>

      <aside className="border-l border-[var(--border-subtle)] pl-6 lg:sticky lg:top-12 lg:self-start">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          preview do template
        </p>
        <p className="mt-3 font-sans text-xs italic leading-relaxed text-[var(--ink-tertiary)]">
          A estrutura que a IA vai seguir literalmente:
        </p>
        <pre className="mt-4 whitespace-pre-wrap break-words rounded-sm bg-[var(--bg-paper-recessed)] p-4 font-serif text-[13px] leading-[1.55] text-[var(--ink-secondary)]">
{`[Público], isso vai mudar como vocês [verbo].

Passei [tempo] testando [ferramenta] voltado pra [área].

Estou falando de [dimensão concreta].

Os N prompts cobrem o que [público] precisa:

→ [dor 1 → solução 1]
→ [dor 2 → solução 2]
→ [dor 3 → solução 3]
→ [dor 4 → solução 4]

Isso aqui não é pra quem [comportamento errado].

Quem aplica IA de verdade em [área] [desfecho].

Comenta ${commentKeyword || 'PALAVRA'} que eu te envio no privado.`}
        </pre>
        <p className="mt-4 border-t border-[var(--border-subtle)] pt-3 font-sans text-xs italic text-[var(--ink-tertiary)]">
          A IA gera 2-3 variantes seguindo essa estrutura, mudando ordem de painpoints, ângulo
          do vocativo e nuances da virada de exclusividade.
        </p>
      </aside>
    </div>
  );
}

const inputClass =
  'w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-[15px] text-[var(--ink-primary)] placeholder:text-[var(--ink-tertiary)] focus:border-[var(--accent-saffron)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-saffron)] disabled:opacity-50';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {label}
      </span>
      {hint && (
        <span className="mt-1 block font-sans text-xs italic text-[var(--ink-tertiary)]">
          {hint}
        </span>
      )}
      <span className="mt-2 block">{children}</span>
    </label>
  );
}
